/*
 * Copyright (c) 2025 curryfirm.com
 */

import fetch from "node-fetch"; // if using Node.js
import { GOOGLE_MAPS_KEY, MOVES } from "../constants.js";
import { ValidationError} from "../error.js";

export const getAllPermutations = (arr) => {
    if (arr.length === 0) return [[]];
    return arr.reduce(
        (acc, item, i) =>
            acc.concat(
                getAllPermutations([...arr.slice(0, i), ...arr.slice(i + 1)]).map(val => [item, ...val])
            ),
        []
    );
}

/**
 * Round to ~100 m precision and return as LatLng objects.
 * @param locs
 * @returns {{latitude: number, longitude: number}[]}
 */
export const locationToLatLng = (...locs) => {
    try {
        const result = locs.map(loc => {
            const roundedLat = Number(Number(loc.lat).toFixed(3));
            const roundedLng = Number(Number(loc.lng).toFixed(3));
            return { latitude: roundedLat, longitude: roundedLng }
        });
        if (result.includes(NaN)) {
            debugger;
        }
        return result;
    }
    catch (exc) {
        throw new ValidationError("Bad location object passed", "locs");
    }
}

export const measureLeg = async (from, to, signal) => {

    const [fromLatLng, toLatLng] = locationToLatLng(from, to);

    const body = {
        // Define endpoints
        origin: { location: { latLng: fromLatLng } },
        destination: { location: { latLng: toLatLng } },

        // Configure routing behavior
        travelMode: "DRIVE",
        routingPreference: "TRAFFIC_AWARE", // avoid jams
    };

    const res = await fetch(
        "https://routes.googleapis.com/directions/v2:computeRoutes",
        {
            method: "POST",
            signal, // allows aborting
            headers: {
                "Content-Type": "application/json",
                "X-Goog-Api-Key": GOOGLE_MAPS_KEY,
                // Limit the payload in the response
                "X-Goog-FieldMask":
                    "routes.distanceMeters,routes.duration",
            },
            body: JSON.stringify(body),
        }
    );

    const text = await res.text();
    let data;
    let jsonError;
    try {
        data = JSON.parse(text);
    } catch (e) {
        data = text;
        jsonError = true;
    }
    if (!res.ok || jsonError) {
        console.error('Request failed:', res.status, res.statusText, `jsonError===${jsonError}`, data);
        throw new Error(
            `Request failed (${res.status}): ${res.statusText} — ${JSON.stringify(data)}`
        );
    }

    const route = data.routes?.[0];

    if (!route) {
        [from, to].forEach(loc => {
            console.error("Unroutable locations:")
            console.dir(loc, { depth: null });
        });
        throw new Error("No route found");
    }

    return {
        distanceMeters: route.distanceMeters ?? Infinity,
        durationSeconds: route.duration ? Number(route.duration.replace("s", "")) : Infinity,
    };
}

export const isValidLatLng = (obj) => {
    return typeof obj === 'object' &&
        obj !== null &&
        typeof obj.LatLng?.latitude === 'number' &&
        typeof obj.LatLng?.longitude === 'number';
}

export const findBestStopover = async (from, to, stops, signal) => {

    let indexOfBest = -1;
    let bestMetric = Infinity;

    for (const [index, stop] of stops.entries()) {
        // Query both legs using same routing logic.
        const leg1 = await measureLeg(from, stop, signal);
        const leg2 = await measureLeg(stop, to, signal);

        // Primary metric: travel time; fallback to distance
        const totalTime = leg1.durationSeconds + leg2.durationSeconds;
        const totalDist = leg1.distanceMeters + leg2.distanceMeters;

        // Primary: minimize travel time; secondary: minimize distance
        const metric = totalTime * 1000 + totalDist / 10;

        if (metric < bestMetric) {
            bestMetric = metric;
            indexOfBest = index;
        }
    }
    return stops[indexOfBest];
}

export const locationToString = location => {
    return [
        location.customer,
        location.street,
        location.city,
        location.state,
        location.zip,
        location.locationId,
        location.lat,
        location.lng,
    ].filter(Boolean).join(', ');
}

export const dumpsterToString = (dumpster) => {
    return [
        dumpster.dumpsterId,
        dumpster.customer,
        dumpster.street,
        dumpster.city,
        dumpster.state,
        dumpster.zip,
        dumpster.locationId,
        dumpster.lat,
        dumpster.lng,
    ].filter(Boolean).join(', ');
}

export const locationsToRouteArgs = (locationSequence) => {
    let result = {};
    if (locationSequence && locationSequence.length > 0) {
        const locLatLngs = locationToLatLng(...locationSequence);
        const start = locLatLngs?.[0];
        const end = locLatLngs?.at(-1);
        const stops = locLatLngs.slice(1, -1);
        result = {start, end, stops};
    }
    return result;
}

export const computeRoute = async (start, end, stops, { signal }) => {
    try {
        const body = {
            origin: { location: { latLng: start } },
            destination: { location: { latLng: end } },
            intermediates: stops.map(s => ({ location: { latLng: s } })),
            travelMode: 'DRIVE',
            optimizeWaypointOrder: false,
        };

        const response = await fetch('https://routes.googleapis.com/directions/v2:computeRoutes', {
            method: 'POST',
            signal,
            headers: {
                'Content-Type': 'application/json',
                'X-Goog-Api-Key': GOOGLE_MAPS_KEY,
                'X-Goog-FieldMask': '*',
            },
            body: JSON.stringify(body),
        });

        if (!response.ok) throw new Error(`HTTP error ${response.status}`);

        return await response.json(); // returns full JSON Routes API payload
    } catch (error) {
        if (error.name === 'AbortError') {
            console.log('Request aborted');
        } else {
            console.error('Route computation failed:', error);
        }
        throw error;
    }
};

const pushMove = (seq, arr, index, action) => arr.push({
    from: seq[index],
    action: action,
    to: seq[index + 1],
});

export const toDumpAndBackSequenceToMoves = (sequence) => {
    if (sequence.length < 5) {
        throw new Error('toDumpAndBackSequenceToMoves: input sequence is less than 5 in length.');
    }

    const moves = [];
    let index = 0;

    // Up to the last dumpster, the pattern is "to (next) dumpster, to dump, (back) to dumpster."
    while (index < sequence.length - 2) {
        const action = (index % 3 !== 1) ? MOVES.TO_DUMPSTER : MOVES.TO_DUMP;
        pushMove(sequence, moves, index, action);
        index++;
    }

    pushMove(sequence, moves, index, MOVES.TO_ENDING_POINT);
    return moves;
}